// The Billing tab of the Account page (laravel BillingController / App\Invoicing). Only the company admin
// (their own company) and the super admin (any company, from the company page) see it; the server checks.

// "Paid 2026-11-03", "Void", "Overdue: was due by 2026-11-08" or "Due by 2026-11-08"; tone for the badge
export function invoiceStatus(invoice) {
	if (!invoice) {
		return { label: '', tone: 'secondary' };
	}
	if (invoice.status === 'paid') {
		return { label: 'Paid' + (invoice.paid_at ? ' ' + invoice.paid_at : ''), tone: 'success' };
	}
	if (invoice.status === 'void') {
		return { label: 'Void', tone: 'secondary' };
	}
	if (invoice.overdue) {
		return { label: 'Overdue: was due by ' + invoice.due_date, tone: 'danger' };
	}
	return { label: 'Due by ' + invoice.due_date, tone: 'warning' };
}

// What hovering an invoice's status shows, or '' for no tooltip: only a void invoice's void type and reason, to
// the super admin only (the server sends the reason to the super admin only). Paid badges already show the date.
export function statusHover(invoice, superAdmin) {
	if (!invoice) {
		return '';
	}
	if (invoice.status === 'void' && superAdmin) {
		return [
			invoice.void_action === 'waive' ? 'Voided and waived' : invoice.void_action === 'reissue' ? 'Voided and reissued' : 'Voided',
			invoice.void_reason && 'Reason: ' + invoice.void_reason,
			// Disputed duty starts removed in its void and reissue
			invoice.removed && invoice.removed.count + ' disputed duty ' + (invoice.removed.count === 1 ? 'start' : 'starts')
				+ ' removed, never charged: ' + invoice.removed.reason
		].filter(Boolean).join('. ');
	}
	return '';
}

// The super admin's line under an invoice's status: "Reminders sent 2026-11-08, 2026-11-14 · suspended the company 2026-11-21"
export function remindersLine(invoice) {
	if (!invoice || !invoice.reminders) {
		return '';
	}
	const parts = [];
	if (invoice.reminders.length > 0) {
		parts.push((invoice.reminders.length === 1 ? 'Reminder sent ' : 'Reminders sent ') + invoice.reminders.join(', '));
	}
	if (invoice.stopped_company_at) {
		parts.push('suspended the company ' + invoice.stopped_company_at);
	}
	return parts.join(' · ');
}

// Who sees the Billing tab: the company admin and the super admin (resellers and district managers do not)
export function canSeeBilling(level) {
	return level === 'a' || level === 'c';
}

// The message for a void reason that cannot be sent, or null
export function noteProblem(note, what) {
	const text = note === null || note === undefined ? '' : String(note).trim();
	if (text === '') {
		return 'Please write ' + what + '.';
	}
	if (text.length > 500) {
		return 'Please keep it to 500 characters.';
	}
	return null;
}

// Saves a PDF the server sent as base64 under its file name
export function downloadPdf(fileName, base64) {
	const bytes = atob(base64);
	const buffer = new Uint8Array(bytes.length);
	for (let i = 0; i < bytes.length; i++) {
		buffer[i] = bytes.charCodeAt(i);
	}
	const url = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// "November 2026" from "2026-11"
export function monthLabel(month) {
	const match = /^(\d{4})-(\d{2})$/.exec(month || '');
	return match ? MONTHS[Number(match[2]) - 1] + ' ' + match[1] : month;
}

function plural(count, one, many) {
	return count + ' ' + (count === 1 ? one : many);
}

// The (i) tooltip of "Expected by the end of the month", from the server's this_month figures (it counts the
// duty starts still expected, also later today): "How this is worked out: 10 duty starts so far (USD 1.00), plus
// 30 more expected by the end of October 2026 (USD 3.00) = about USD 4.00. ..."
export function estimateText(month) {
	const estimate = month.estimate;
	const soFar = 'How this is worked out: ' + plural(month.duty_starts, 'duty start', 'duty starts') + ' so far (USD ' + month.usd + ')';
	const end = ' Guards who are Off duty are not counted. New duty starts show here within 15 minutes.';
	if (estimate.expected > 0) {
		return soFar + ', plus ' + estimate.expected + ' more expected by the end of ' + monthLabel(month.month) + ' (USD ' + estimate.added_usd
			+ ') = about USD ' + estimate.usd + '.' + end;
	}
	return soFar + '; no more duty starts are expected this month.' + end;
}

// Under the billing line: who the invoices are addressed to (the Contact Person's full name, "Attn:" on them)
export function contactLine(billing) {
	return billing && billing.contact_name
		? 'Invoices are emailed to the company admin, Attn: ' + billing.contact_name + '.'
		: 'Invoices are emailed to the company admin; no contact person is set on Company settings.';
}

// The line at the top of the tab: "Billed from 1 September 2026, at USD 0.10 per duty start." (the server's billing)
export function billingLine(billing) {
	if (!billing || !billing.starts_on) {
		return 'Billing has not started for this company yet.';
	}
	return 'Billed from ' + billing.starts_on_label + ', at USD ' + billing.rate_usd + ' per duty start'
		+ (billing.clients_own_price ? ' (some clients have their own price)' : '') + '.';
}

export const RECENT = 'recent';

// The first month of "the last 12 months" up to this month: "2025-12" from "2026-11"
export function recentFrom(month) {
	const match = /^(\d{4})-(\d{2})$/.exec(month || '');
	if (!match) {
		return '0000-00';
	}
	const index = Number(match[1]) * 12 + Number(match[2]) - 1 - 11;
	return Math.floor(index / 12) + '-' + String((index % 12) + 1).padStart(2, '0');
}

// The years of the invoices older than the last 12 months, newest first, for the year picker
export function olderYears(invoices, month) {
	const from = recentFrom(month);
	const years = (invoices || []).filter((invoice) => invoice.period < from).map((invoice) => invoice.period.slice(0, 4));
	return Array.from(new Set(years)).sort().reverse();
}

function newestFirst(a, b) {
	return a.period === b.period ? b.id - a.id : (a.period < b.period ? 1 : -1);
}

// The invoices to show: unpaid ones always, pinned at the top (overdue first), then the paid and void ones of the
// last 12 months (view RECENT) or of one year ('2025'), newest first. -> { pinned, rest }
export function visibleInvoices(invoices, view, month) {
	const all = invoices || [];
	const pinned = all.filter((invoice) => invoice.status === 'unpaid')
		.sort((a, b) => (a.overdue === b.overdue ? newestFirst(a, b) : (a.overdue ? -1 : 1)));
	const from = recentFrom(month);
	const rest = all.filter((invoice) => invoice.status !== 'unpaid')
		.filter((invoice) => (view === RECENT ? invoice.period >= from : invoice.period.slice(0, 4) === view))
		.sort(newestFirst);
	return { pinned, rest };
}
